import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Shield, Activity, FileText, ExternalLink } from 'lucide-react';
import { theme, cardStyle, inputStyle, btnStyle } from '../theme';

// ═══════════════════════════════════════════════════════════════════
//  CLINICAL QUALITY (Phase 14)
//   - Sterilization pass rate summary
//   - Treatment outcomes summary + complication breakdown
//   - NABH audit HTML report link per month
// ═══════════════════════════════════════════════════════════════════

const ClinicalQualityPage = ({ API_BASE }) => {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [steril, setSteril] = useState(null);
  const [outcomes, setOutcomes] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const from = `${month}-01`;
      const to = new Date(new Date(month + '-01').setMonth(new Date(month + '-01').getMonth() + 1) - 86400000).toISOString().slice(0, 10);
      const [s, o] = await Promise.all([
        axios.get(`${API_BASE}/clinical-quality/sterilization-summary`, { params: { from, to } }),
        axios.get(`${API_BASE}/clinical-quality/outcomes-summary`, { params: { from, to } }),
      ]);
      setSteril(s.data); setOutcomes(o.data);
    } catch (e) { alert('Load failed: ' + (e.response?.data?.error || e.message)); }
    finally { setLoading(false); }
  }, [API_BASE, month]);

  useEffect(() => { load(); }, [load]);

  const openNABHReport = () => {
    window.open(`${API_BASE}/clinical-quality/nabh-report?month=${month}`, '_blank');
  };

  const monthOptions = () => {
    const out = []; const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const v = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
      out.push({ value: v, label: d.toLocaleString('en-IN', { month:'short', year:'numeric' }) });
    }
    return out;
  };

  return (
    <div style={{ padding:20 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16, flexWrap:'wrap', gap:12 }}>
        <h2 style={{ color: theme.navy, margin:0 }}>🏥 Clinical Quality</h2>
        <div style={{ display:'flex', gap:8 }}>
          <select value={month} onChange={e => setMonth(e.target.value)} style={{ ...inputStyle, width:180 }}>
            {monthOptions().map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <button onClick={openNABHReport} style={{ ...btnStyle, background: theme.primary, color:'white', display:'flex', gap:6, alignItems:'center' }}>
            <FileText size={14}/> Open NABH Report
          </button>
        </div>
      </div>

      <div style={{ ...cardStyle, marginBottom:12, background: theme.primaryLight, border:`1px solid ${theme.primary}` }}>
        <div style={{ fontSize:'0.85rem', color: theme.navy, lineHeight:1.6 }}>
          <b>Quality KPIs:</b> Sterilization pass rate is the core infection-control indicator. Treatment success rate + satisfaction ratings come from post-op outcome follow-ups. NABH report pulls all of these + staff attendance + training into one HTML document for monthly audit.
        </div>
      </div>

      {loading && <div style={{ padding:40, textAlign:'center', color:'#888' }}>Loading…</div>}

      {/* Top KPI row */}
      {steril && outcomes && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:16 }}>
          <KPICard icon={Shield} label="Sterilization Pass" value={steril.passRate != null ? `${steril.passRate}%` : 'N/A'} sub={`${steril.pass}/${steril.total} cycles`} color={theme.success}/>
          <KPICard icon={Activity} label="Treatment Success" value={outcomes.successRate != null ? `${outcomes.successRate}%` : 'N/A'} sub={`${outcomes.byStatus?.success || 0}/${outcomes.total} outcomes`} color={theme.info}/>
          <KPICard icon={Activity} label="Avg Satisfaction" value={outcomes.avgSatisfaction != null ? `⭐ ${outcomes.avgSatisfaction}/5` : 'N/A'} sub="Patient-reported" color={theme.warning}/>
          <KPICard icon={Activity} label="Avg Pain Level" value={outcomes.avgPainLevel != null ? `${outcomes.avgPainLevel}/10` : 'N/A'} sub="Post-op follow-up" color={theme.danger}/>
        </div>
      )}

      {/* Sterilization breakdown */}
      {steril && (
        <div style={{ ...cardStyle, marginBottom:16 }}>
          <h3 style={{ color: theme.navy, margin:'0 0 10px' }}>🧴 Sterilization Summary</h3>
          <div style={{ display:'flex', gap:16, fontSize:14 }}>
            <div>Total cycles: <b>{steril.total}</b></div>
            <div style={{ color: theme.success }}>Pass: <b>{steril.pass}</b></div>
            <div style={{ color: theme.danger }}>Fail: <b>{steril.fail}</b></div>
          </div>
          {steril.fail > 0 && (
            <div style={{ marginTop:10, padding:10, background:'#fee2e2', borderRadius:8, color:'#991b1b', fontSize:13 }}>
              ⚠ {steril.fail} failed cycles need reprocessing + root-cause analysis before NABH sign-off.
            </div>
          )}
        </div>
      )}

      {/* Outcomes breakdown */}
      {outcomes && outcomes.total > 0 && (
        <div style={{ ...cardStyle, marginBottom:16 }}>
          <h3 style={{ color: theme.navy, margin:'0 0 10px' }}>🦷 Treatment Outcomes</h3>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:8, marginBottom:12 }}>
            {Object.entries(outcomes.byStatus).map(([k, v]) => (
              <div key={k} style={{ padding:10, background:'#f9fafb', borderRadius:8, textAlign:'center' }}>
                <div style={{ fontSize:22, fontWeight:700, color: theme.navy }}>{v}</div>
                <div style={{ fontSize:11, color:'#666', textTransform:'capitalize' }}>{k}</div>
              </div>
            ))}
          </div>

          {Object.keys(outcomes.complicationCounts || {}).length > 0 && (
            <div>
              <h4 style={{ color: theme.navy, margin:'14px 0 6px', fontSize:14 }}>Complications reported:</h4>
              <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                {Object.entries(outcomes.complicationCounts).sort((a,b) => b[1] - a[1]).map(([name, count]) => (
                  <span key={name} style={{ padding:'4px 10px', borderRadius:12, background:'#fef3c7', color:'#92400e', fontSize:12, fontWeight:600 }}>
                    {name}: {count}
                  </span>
                ))}
              </div>
            </div>
          )}

          {outcomes.retreatmentsNeeded > 0 && (
            <div style={{ marginTop:10, padding:10, background:'#fef3c7', borderRadius:8, color:'#92400e', fontSize:13 }}>
              ⚠ {outcomes.retreatmentsNeeded} treatments flagged for re-treatment.
            </div>
          )}
        </div>
      )}

      {outcomes && outcomes.total === 0 && (
        <div style={{ ...cardStyle, textAlign:'center', color:'#888', padding:40 }}>
          No outcome follow-ups recorded for {month}. Start logging outcomes in Treatment completion to populate quality KPIs.
        </div>
      )}

      <div style={{ marginTop:16, padding:14, background:'#eff6ff', borderRadius:10, border:`1px solid ${theme.info}`, display:'flex', gap:10, alignItems:'center' }}>
        <FileText size={20} color={theme.info}/>
        <div style={{ flex:1, fontSize:13, color: theme.navy }}>
          Generate printable NABH audit report covering all quality indicators for {month}. Includes sterilization, outcomes, staff attendance, training certifications.
        </div>
        <button onClick={openNABHReport} style={{ ...btnStyle, background: theme.primary, color:'white', display:'flex', gap:6, alignItems:'center' }}>
          <ExternalLink size={14}/> Open Report
        </button>
      </div>
    </div>
  );
};

const KPICard = ({ icon: Icon, label, value, sub, color }) => (
  <div style={{ ...cardStyle, padding:14, borderLeft:`4px solid ${color}` }}>
    <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:4 }}>
      <Icon size={16} color={color}/>
      <div style={{ fontSize:11, color:'#888', textTransform:'uppercase', letterSpacing:1 }}>{label}</div>
    </div>
    <div style={{ fontSize:22, fontWeight:700, color: theme.navy }}>{value}</div>
    <div style={{ fontSize:11, color:'#888', marginTop:2 }}>{sub}</div>
  </div>
);

export default ClinicalQualityPage;
